import { Link } from "react-router-dom";
import { Mail, Phone, Clock, ArrowUpRight, MapPin } from "lucide-react";
import { WhatsAppButton } from "@/components/ui-extras/WhatsApp";

export default function Footer() {
  return (
    <footer className="mt-24 bg-petrol text-cream relative overflow-hidden grain" data-testid="site-footer">
      <div aria-hidden="true" className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-gold/15 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-goldSoft/10 blur-3xl" />

      {/* Editorial signoff */}
      <div className="container-x relative pt-20 md:pt-24 pb-12">
        <p className="font-display text-[clamp(2.2rem,6.5vw,5rem)] leading-[0.98] tracking-tight text-balance max-w-5xl">
          Perspectief <em className="italic text-gold">begint</em>
          <br className="hidden md:block" />
          <span> </span>vandaag.
        </p>
        <p className="mt-6 text-cream/70 text-lg max-w-2xl">
          Stichting FIN — Future Is Now. Voor perspectief, bestaanszekerheid en gelijke kansen.
        </p>
      </div>

      <div className="container-x relative pb-14 md:pb-16 grid gap-12 md:grid-cols-12 border-t border-cream/10 pt-14">
        <div className="md:col-span-4">
          <img
            src="/logo-fin.png"
            alt="Stichting FIN"
            className="h-24 w-auto object-contain bg-cream/95 rounded-2xl p-2 shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
            width={512}
            height={512}
            loading="lazy"
            data-testid="brand-logo-footer"
          />
          <p className="text-cream/70 text-pretty leading-relaxed max-w-md mt-6 text-[15px]">
            Praktische ondersteuning, persoonlijke begeleiding en maatschappelijke projecten — voor mensen en gemeenschappen die
            vooruit willen.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Programma's</p>
          <ul className="space-y-3 text-cream/85 text-[15px]">
            <li><Link to="/wat-we-doen#herstel" className="hover:text-gold transition" data-testid="footer-link-herstel">FIN Herstel</Link></li>
            <li><Link to="/wat-we-doen#bestaanszekerheid" className="hover:text-gold transition" data-testid="footer-link-bestaanszekerheid">FIN Bestaanszekerheid</Link></li>
            <li><Link to="/wat-we-doen#groei-kansen" className="hover:text-gold transition" data-testid="footer-link-groei">FIN Groei & Kansen</Link></li>
            <li><Link to="/wat-we-doen#community" className="hover:text-gold transition" data-testid="footer-link-community">FIN Community</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Navigatie</p>
          <ul className="space-y-3 text-cream/85 text-[15px]">
            <li><Link to="/projecten" className="hover:text-gold transition" data-testid="footer-link-projecten">Projecten</Link></li>
            <li><Link to="/over-fin" className="hover:text-gold transition" data-testid="footer-link-over">Over FIN</Link></li>
            <li><Link to="/voor-gemeenten" className="hover:text-gold transition" data-testid="footer-link-gemeenten">Voor partners</Link></li>
            <li><Link to="/steun" className="hover:text-gold transition" data-testid="footer-link-steun">Steun FIN</Link></li>
            <li><Link to="/hulp-nodig" className="hover:text-gold transition" data-testid="footer-link-hulp">Hulp nodig?</Link></li>
            <li><Link to="/contact" className="hover:text-gold transition" data-testid="footer-link-contact">Contact</Link></li>
            <li><Link to="/privacy" className="hover:text-gold transition" data-testid="footer-link-privacy">Privacy</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Contact</p>
          <ul className="space-y-4 text-cream/85 text-[15px]">
            <li className="flex items-start gap-3">
              <Mail size={17} className="mt-0.5 text-gold shrink-0" />
              <a href="mailto:info@stichting-fin.nl" className="hover:text-gold transition" data-testid="footer-email">
                info@stichting-fin.nl
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={17} className="mt-0.5 text-gold shrink-0" />
              <a href="tel:+31643828430" className="hover:text-gold transition" data-testid="footer-phone">
                +31 6 43 82 84 30
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={17} className="mt-0.5 text-gold shrink-0" />
              <span>Rotterdam &<br />landelijk actief</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={17} className="mt-0.5 text-gold shrink-0" />
              <span>Ma – vr · 09:00 – 17:00</span>
            </li>
            <li className="flex gap-2 pt-2">
              <Link
                to="/hulp-nodig"
                className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-5 py-2.5 text-sm font-semibold"
                data-testid="footer-cta-hulp"
              >
                Ik zoek hulp <ArrowUpRight size={15} />
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10 relative">
        <div className="container-x py-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-xs text-cream/50">
          <p>© {new Date().getFullYear()} Stichting FIN — Future Is Now. KvK 89903293</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-cream transition">Privacy</Link>
            <span className="text-cream/30">·</span>
            <WhatsAppButton variant="ghost" size="sm" testId="footer-whatsapp-button" />
            <Link to="/admin/login" className="hover:text-cream transition" data-testid="footer-admin-link">
              Beheer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
